import React from "react";
import { StyleSheet, Text, TouchableNativeFeedback, View } from "react-native";
import dayjs from "dayjs";
import { useNavigation } from "@react-navigation/native";
import { CrateV2, Gullak, ItemsV2, Returned } from "@assets/svg/MyDeliveries";
import { MY_DELIVERY_EVENTS } from "@constants/analyticsEventsCT";
import t from "@constants/localization";
import { colors } from "@constants/theme";
import analytics from "@services/analytics";
import { useSelector } from "react-redux";
import { getReturnAmount } from "../utils";
import {
  getItemsNotReturnedText,
  getCrateListFromCrateIds,
} from "../DeliveryReturn/utils";
import CratePacketItemsInfo from "../components/CratePacketItemsInfo";
import RightArrow from "@assets/svg/RightArrow";

const PATH = "screens/MyDeliveries/DeliveryReturn/OverView";

const ReturnCard = ({
  returnDetails,
  date,
  spokeName,
  setInfoTooltipVisible,
  setInfoTooltipData,
}) => {
  const navigation = useNavigation();
  const returnState = returnDetails?.state;
  const { features = {} } = useSelector(({ configReducer }) => configReducer);
  const returnCardContent = [
    {
      field: "crate",
      icon: <CrateV2 />,
      text: t(`${PATH}/crate`),
      value: returnDetails?.noOfCrates || 0,
      list: returnDetails?.noOfCrates
        ? getCrateListFromCrateIds(returnDetails.crates_list)
        : [],
    },
    {
      field: "item",
      icon: <ItemsV2 />,
      text: t(`${PATH}/item`),
      value: returnDetails?.noOfProducts || 0,
    },
    ...(features?.previous_returned_amount
      ? [
          {
            field: "amount",
            icon: <Gullak stats={returnState} />,
            text:
              t(`${PATH}/${getReturnAmount(returnDetails?.amount).key}`) || "",
            value:
              (returnDetails?.amount &&
                getReturnAmount(returnDetails?.amount).amount) ||
              0,
            symbol: <Text style={styles.symbol}>{t(`${PATH}/rs`)}</Text>,
          },
        ]
      : []),
  ];

  const handleReturnCardPress = () => {
    navigation.navigate("Returns", {
      date: date || dayjs().format("YYYY-MM-DD"),
      spokeName,
      tab: "Current day",
    });

    analytics.trackEvent(MY_DELIVERY_EVENTS.VIEW_DETAILS_CLICKED, {
      screen: "Delivery Page",
      tab: "Current day",
      section: "Return",
    });
  };

  const onPressInfo = (data) => {
    setInfoTooltipVisible(true);
    setInfoTooltipData(data);
  };

  return (
    <View style={styles.cardContainer}>
      <TouchableNativeFeedback
        testID="handleReturnCardPress"
        background={TouchableNativeFeedback.Ripple("rgba(0, 0, 0, 0.05)")}
        onPress={() => handleReturnCardPress()}
      >
        <View style={styles.card}>
          <View
            style={[
              styles.headerContainer,
              {
                backgroundColor:
                  returnState === "Return"
                    ? "rgba(0, 171, 89, 0.04)"
                    : "rgba(0, 0, 0, 0.04)",
                flex: 1,
                justifyContent: "space-between",
              },
            ]}
          >
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Returned status={returnState} />
              <Text
                style={
                  returnState === "Return"
                    ? styles.greenHeaderText
                    : styles.greyHeaderText
                }
              >
                {t(`${PATH}/${returnState}`)}
              </Text>
            </View>
          </View>
          {returnDetails?.itemsNotReturned ||
          returnDetails?.cratesNotReturned ? (
            <View style={styles.orderNotTaken}>
              <Text style={styles.orderNotTakenText}>
                {getItemsNotReturnedText(returnDetails)}
              </Text>
            </View>
          ) : null}
          <CratePacketItemsInfo
            Content={returnCardContent}
            onPressInfo={onPressInfo}
          />
          <View style={styles.buttonStyle}>
            <View />
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Text style={styles.viewDetailsText}>
                {t(`${PATH}/viewDetails`)}
              </Text>
              <RightArrow
                stroke={colors.extraLightBlack}
                fill={colors.extraLightBlack}
              />
            </View>
          </View>
        </View>
      </TouchableNativeFeedback>
    </View>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  card: {
    backgroundColor: colors.white,
    borderWidth: 0.5,
    borderRadius: 10,
    elevation: 2,
    borderColor: colors.lighterBlack,
  },
  headerContainer: {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    padding: 10,
    flexDirection: "row",
    alignItems: "center",
  },
  buttonStyle: {
    justifyContent: "space-between",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderTopWidth: 0.8,
    borderColor: colors.lighterBlack,
    width: "100%",
  },
  orderNotTaken: {
    justifyContent: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: "#FFEEEB",
    marginHorizontal: 16,
    marginVertical: 12,
    borderRadius: 4,
  },
  orderNotTakenText: {
    color: "#FF2E00",
    fontSize: 12,
    lineHeight: 18,
    fontFamily: "NotoSansDevanagari-Bold",
  },
  symbol: {
    fontSize: 16,
    fontFamily: "NotoSansDevanagari-Regular",
    lineHeight: 26,
  },
  viewDetailsText: {
    fontFamily: "NotoSansDevanagari-Bold",
    fontSize: 14,
    lineHeight: 20,
    marginRight: 8,
  },
  greenHeaderText: {
    fontSize: 12,
    fontFamily: "NotoSansDevanagari-Bold",
    lineHeight: 18,
    color: colors.plain_green,
    marginHorizontal: 4,
  },
  greyHeaderText: {
    fontSize: 12,
    fontFamily: "NotoSansDevanagari-Bold",
    lineHeight: 18,
    color: colors.lightBlack,
    alignItems: "center",
    marginHorizontal: 4,
  },
});

export default ReturnCard;
